import { useEffect, useState } from 'react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { packOf } from './shizaiApi';

const yen = (n) => (n == null ? '' : '¥' + Number(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
const num = (n) => (n == null ? '' : Number(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
const slashDate = (iso) => (iso ? String(iso).slice(0, 10).replaceAll('-', '/') : '');

function useBarcode(value) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!value) return;
    try {
      const c = document.createElement('canvas');
      JsBarcode(c, String(value), { format: 'CODE128', width: 2, height: 46, margin: 0, displayValue: false });
      setUrl(c.toDataURL('image/png'));
    } catch { setUrl(''); }
  }, [value]);
  return url;
}

function useQr(text) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!text) return;
    QRCode.toDataURL(text, { margin: 0, width: 120 }).then(setUrl).catch(() => setUrl(''));
  }, [text]);
  return url;
}

/* ---------- small building blocks ---------- */

const SbMark = () => (
  <span className="sb-mark">SENSBEY</span>
);

const TitleBox = ({ children, small }) => (
  <div className={small ? 'title-box title-box-sm' : 'title-box'}>{children}</div>
);

function MetaBoxes({ items }) {
  return (
    <table className="meta-box">
      <tbody>
        <tr>{items.map((i) => <th key={i.label} className={i.thick ? 'thick' : ''}>{i.label}</th>)}</tr>
        <tr>{items.map((i) => <td key={i.label} className={i.thick ? 'thick' : ''}>{i.value || '\u00A0'}</td>)}</tr>
      </tbody>
    </table>
  );
}

const StampGrid = ({ n, h }) => (
  <table className="stamp-grid" style={h ? { height: h } : undefined}>
    <tbody><tr>{Array.from({ length: n }).map((_, i) => <td key={i} />)}</tr></tbody>
  </table>
);

/* ---------- 発注書 / 納品書 item bands ---------- */

function BandA({ line }) {
  const partCell = line.drawing_no
    ? (line.part_no ? `${line.part_no} / ${line.drawing_no}` : line.drawing_no)
    : (line.part_no || '\u00A0');
  return (
    <table className="doc-t band-a">
      <colgroup><col style={{ width: '28.5%' }} /><col style={{ width: '28.6%' }} /><col style={{ width: '35.3%' }} /><col style={{ width: '7.6%' }} /></colgroup>
      <tbody>
        <tr><th>製番/製品No.</th><th className="th-sm">ユニットNo./部品No.</th><th>品番/図番</th><th>改訂</th></tr>
        <tr className="tall2">
          <td>{line.seibang || '\u00A0'}</td>
          <td className="vbottom">{line.unit_no || '\u00A0'}</td>
          <td>{partCell}</td>
          <td className="vbottom">{line.revision || '\u00A0'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function BandB({ line }) {
  return (
    <table className="doc-t">
      <colgroup><col style={{ width: '51.5%' }} /><col style={{ width: '21.3%' }} /><col style={{ width: '11.1%' }} /><col style={{ width: '16.1%' }} /></colgroup>
      <tbody>
        <tr><th>品名/形式寸法</th><th>材質/メーカ</th><th>支給/単価区分</th><th className="th-sm">見積依頼明細No./重量</th></tr>
        <tr className="tall2">
          <td>{line.name || '\u00A0'}</td>
          <td>{line.material || '\u00A0'}</td>
          <td className="vbottom">{line.supply_type || '正式単価'}</td>
          <td>{'\u00A0'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function QtyBandOrder({ order, line }) {
  const pk = packOf(line);
  return (
    <table className="doc-t">
      <colgroup><col style={{ width: '41%' }} /><col style={{ width: '15%' }} /><col style={{ width: '7.2%' }} /><col style={{ width: '20.6%' }} /><col style={{ width: '16.2%' }} /></colgroup>
      <tbody>
        <tr>
          <td rowSpan={2} className="vtop"><span className="cell-lbl">納入場所</span><br />{order.delivery_location || ''}</td>
          <th>発注数</th><th>単位</th><th>発注単価</th><th>発注金額</th>
        </tr>
        <tr className="big-num" style={{ height: '5.8mm' }}>
          <td className="num">{pk ? <>{num(pk.packs)}<span className="pk-sub">({num(pk.pieces)}{line.uom || '個'})</span></> : num(line.qty)}</td>
          <td className="left">{pk ? pk.packUom : (line.uom || '')}</td>
          <td className="num">{pk ? yen(pk.packPrice) : yen(line.unit_price)}</td>
          <td className="num">{yen(line.amount)}</td>
        </tr>
        <tr style={{ height: '2.6mm' }}>
          <td rowSpan={2} className="vtop" style={{ verticalAlign: 'top' }}><span className="cell-lbl">備考/未定理由　決定予定日　決定予定</span><br />{order.notes || ''}</td>
          <td colSpan={3} className="open-mid" />
          <th>消費税</th>
        </tr>
        <tr>
          <td colSpan={3} className="open-bot" />
          <td style={{ height: '5.2mm' }}>{'\u00A0'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function QtyBandNouhin({ order, line }) {
  const pk = packOf(line);
  return (
    <>
      <table className="doc-t">
        <colgroup><col style={{ width: '41%' }} /><col style={{ width: '15%' }} /><col style={{ width: '7.2%' }} /><col style={{ width: '20.6%' }} /><col style={{ width: '16.2%' }} /></colgroup>
        <tbody>
          <tr>
            <td className="vtop" style={{ height: '3.4mm' }}><span className="cell-lbl">納入場所</span> {order.delivery_location || ''}</td>
            <th>発注数</th><th>単位</th><th>発注単価</th><th>発注金額</th>
          </tr>
          <tr className="big-num">
            <td style={{ height: '8mm', verticalAlign: 'top' }}><span className="cell-lbl">備考</span></td>
            <td className="num">{pk ? <>{num(pk.packs)}<span className="pk-sub">({num(pk.pieces)}{line.uom || '個'})</span></> : num(line.qty)}</td>
            <td className="left">{pk ? pk.packUom : (line.uom || '')}</td>
            <td className="num">{pk ? yen(pk.packPrice) : yen(line.unit_price)}</td>
            <td className="num">{yen(line.amount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="inbound-row">
        <div className="stamp-square" />
        <table className="doc-t thick-block">
          <colgroup><col style={{ width: '13%' }} /><col style={{ width: '20%' }} /><col style={{ width: '20%' }} /><col style={{ width: '27%' }} /><col style={{ width: '20%' }} /></colgroup>
          <tbody>
            <tr><td rowSpan={2} className="center vmiddle kanno">完納・分納</td><th>入荷日</th><th>入荷数</th><th>入荷単価</th><th>入荷金額</th></tr>
            <tr className="tall2"><td>{'\u00A0'}</td><td /><td /><td /></tr>
          </tbody>
        </table>
        <table className="doc-t tax-col">
          <tbody>
            <tr><th>消費税</th></tr>
            <tr className="tall2"><td>{'\u00A0'}</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- 受領書 / 現品票 compact table ---------- */

function CompactTable({ order, line, withBiko, withStamp }) {
  const pk = packOf(line);
  return (
    <>
      <table className="doc-t">
        <colgroup><col style={{ width: '40%' }} /><col style={{ width: '48%' }} /><col style={{ width: '12%' }} /></colgroup>
        <tbody>
          <tr><th className="th-sm">製番/製品No./ユニットNo.</th><th>品番/図番</th><th>改訂</th></tr>
          <tr className="tallA">
            <td>{line.seibang || '\u00A0'}{line.unit_no ? <><br />{line.unit_no}</> : null}</td>
            <td>{line.drawing_no ? (line.part_no ? `${line.part_no} / ${line.drawing_no}` : line.drawing_no) : (line.part_no || '\u00A0')}</td>
            <td className="vbottom">{line.revision || '\u00A0'}</td>
          </tr>
          <tr><th colSpan={3}>品名/形式寸法</th></tr>
          <tr className="tall2"><td colSpan={3}>{line.name || '\u00A0'}</td></tr>
        </tbody>
      </table>
      <table className="doc-t">
        <colgroup><col style={{ width: '43%' }} /><col style={{ width: '30%' }} /><col style={{ width: '27%' }} /></colgroup>
        <tbody>
          <tr><th>材質/メーカ</th><th>支給/単価区分</th><th>重量/納期</th></tr>
          <tr className="tall2">
            <td>{line.material || '\u00A0'}</td>
            <td className="vbottom">{line.supply_type || '正式単価'}</td>
            <td className="vbottom num">{slashDate(order.due_date)}</td>
          </tr>
        </tbody>
      </table>
      {withBiko && <div className="biko-row">備考:</div>}
      <div className="inbound-row">
        <table className="doc-t thick-block">
          <colgroup><col style={{ width: '17%' }} /><col style={{ width: '31%' }} /><col style={{ width: '36%' }} /><col style={{ width: '16%' }} /></colgroup>
          <tbody>
            <tr><td rowSpan={2} className="center vmiddle kanno">完納・分納</td><th>入荷日</th><th>入荷数</th><th>単位</th></tr>
            <tr className="tall2"><td>{'\u00A0'}</td><td /><td className="big-num left">{pk ? <>{pk.packUom}<span className="pk-sub pk-sub-block">1{pk.packUom}={pk.packSize}{line.uom || '個'}</span></> : (line.uom || '個')}</td></tr>
          </tbody>
        </table>
        {withStamp && <div className="stamp-square stamp-square-sm" />}
      </div>
    </>
  );
}

/* ---------- party blocks ---------- */

function VendorBlockOrder({ s }) {
  return (
    <div className="party">
      {s.zip && <div>{s.zip.startsWith('〒') ? s.zip : `〒${s.zip}`}</div>}
      {s.address && <div>{s.address}</div>}
      <div className="party-name">{s.name || ''}</div>
      <div className="party-contact">{s.contact ? `${s.contact} 様` : '担当者 様'}</div>
      <div className="party-tel">{s.tel ? `TEL:${s.tel}` : ''}<span className="telgap" />{s.fax ? `FAX:${s.fax}` : ''}</div>
    </div>
  );
}

function BuyerBlockOrder({ b }) {
  return (
    <div className="party">
      <div className="party-name">{b.company || '株式会社センスビー'}</div>
      <div className="party-pad">{b.zip || ''}</div>
      <div>{b.address || ''}</div>
      <div className="party-two party-pad">
        <span>{b.tel ? `TEL:${b.tel}` : ''}<br />{b.dept || '資材部'}</span>
        <span>{b.fax ? `FAX:${b.fax}` : ''}<br />{b.contact || '深澤　信太郎'}</span>
      </div>
    </div>
  );
}

/* ---------- one A4 page = one order line ---------- */

function DocPage({ order, line, idx, count, barUrl, qrUrl, printedAt }) {
  const s = order.supplier || {};
  const b = order.buyer || {};
  const orderTitle = order.order_type === 'subcontract' ? '外注発注書' : '発　注　書';
  return (
    <div className="doc-page" data-testid={`doc-page-${idx}`}>

      {/* ============ 1. 発注書 ============ */}
      <section className="doc-sec">
        <div className="head-grid">
          <div className="head-left"><MetaBoxes items={[{ label: '発注日', value: slashDate(order.order_date) }]} /><SbMark /></div>
          <TitleBox>{orderTitle}</TitleBox>
          <div className="head-right">
            <MetaBoxes items={[{ label: '注文No.', value: order.order_no }, { label: '納期', value: slashDate(order.due_date), thick: true }]} />
          </div>
        </div>
        <div className="parties">
          <VendorBlockOrder s={s} />
          <BuyerBlockOrder b={b} />
        </div>
        <div className="table-wrap">
          <div className="stamp-abs"><StampGrid n={3} /></div>
          <div className="band-a-wrap"><BandA line={line} /></div>
          <BandB line={line} />
          <QtyBandOrder order={order} line={line} />
        </div>
        <div className="inquiry">■{(b.inquiry || '').replace(/^■/, '')}</div>
      </section>

      <div className="cut-line" />

      {/* ============ 2. 納品書兼請求書 ============ */}
      <section className="doc-sec">
        <div className="head-grid">
          <div className="head-left"><SbMark /></div>
          <TitleBox>納品書兼請求書</TitleBox>
          <div className="head-right">
            <MetaBoxes items={[{ label: '取引先コード', value: s.code }, { label: '注文No.', value: order.order_no }, { label: '納期', value: slashDate(order.due_date), thick: true }]} />
          </div>
        </div>
        {barUrl && <img src={barUrl} className="barcode barcode-nouhin" alt="" />}
        <div className="parties parties-nouhin">
          <div className="party">
            <div className="party-name">{b.company || '株式会社センスビー'}</div>
            <div>{b.dept || '資材部'}</div>
            <div>{b.contact || '深澤　信太郎'}　様</div>
          </div>
          <div className="party party-mid"><div className="party-name">{s.name || ''}</div></div>
        </div>
        <div className="table-wrap">
          <div className="stamp-abs"><StampGrid n={3} /></div>
          <div className="band-a-wrap"><BandA line={line} /></div>
          <BandB line={line} />
          <QtyBandNouhin order={order} line={line} />
        </div>
      </section>

      <div className="cut-line" />

      {/* ============ 3 + 4. 受領書 / 現品票 ============ */}
      <div className="doc-pair">
        <section className="doc-half">
          <div className="half-head">
            <TitleBox small>受　領　書</TitleBox>
            <div className="half-head-right">
              <SbMark />
              <MetaBoxes items={[{ label: '注文No.', value: order.order_no }]} />
            </div>
          </div>
          <div className="half-vendor">{s.name || ''}　御中</div>
          <CompactTable order={order} line={line} />
          <div className="half-footer">
            <div>
              <div className="party-name">{b.company || '株式会社センスビー'}</div>
              <div className="party-pad">{b.dept || '資材部'}</div>
              <div>{b.contact || '深澤　信太郎'}</div>
            </div>
            <StampGrid n={2} h="14mm" />
          </div>
        </section>

        <section className="doc-half">
          <div className="half-head">
            <TitleBox small>現　品　票</TitleBox>
            <div className="half-head-right">
              <SbMark />
              <MetaBoxes items={[{ label: '注文No.', value: order.order_no }]} />
            </div>
          </div>
          {barUrl && <img src={barUrl} className="barcode barcode-genpin" alt="" />}
          {s.code && <div className="half-code">{s.code}</div>}
          <div className="half-vendor">{s.name || ''}</div>
          <CompactTable order={order} line={line} withBiko withStamp />
          <div className="half-footer genpin-footer">
            <div className="party-name">{b.company || '株式会社センスビー'}</div>
          </div>
        </section>
      </div>

      {/* ============ Sensbey footer ============ */}
      <div className="sb-footer">
        <div className="sb-footer-text">
          <span className="sb-mark">SENSBEY</span>
          <span>Generated by SENSBEY Web App · sensbey.tech · 印刷: {printedAt}{count > 1 ? ` · 明細 ${idx + 1}/${count}` : ''} · 注文No. {order.order_no}</span>
        </div>
        {qrUrl && <img src={qrUrl} className="sb-qr" alt="" />}
      </div>
    </div>
  );
}

export default function DocumentSheet({ order }) {
  const barUrl = useBarcode(order?.order_no);
  const qrUrl = useQr(order ? `${window.location.origin}/shizai/orders/${order.id}/print` : '');
  const [printedAt] = useState(() => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  if (!order) return null;
  const lines = (order.lines && order.lines.length ? order.lines : [{}]);
  return (
    <div className="doc-sheet" data-testid="document-sheet">
      <style>{DOC_CSS}</style>
      {lines.map((line, i) => (
        <DocPage key={`${line.part_no || line.name || ''}-${i}`} order={order} line={line} idx={i} count={lines.length} barUrl={barUrl} qrUrl={qrUrl} printedAt={printedAt} />
      ))}
    </div>
  );
}

const DOC_CSS = `
.doc-sheet { color: #000; font-family: "MS PGothic","MS Gothic","Yu Gothic","Hiragino Kaku Gothic ProN","Meiryo",sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.doc-page { width: 210mm; min-height: 287mm; margin: 0 auto 8mm; background: #fff; box-shadow: 0 1px 8px rgba(0,0,0,.15); padding: 6mm 10mm 2mm; box-sizing: border-box; display: flex; flex-direction: column; }
.doc-sec { margin-bottom: 0; }

/* header row */
.head-grid { display: grid; grid-template-columns: 1fr auto 1fr; align-items: flex-start; margin-bottom: 1mm; }
.head-left { display: flex; align-items: flex-start; gap: 3mm; }
.head-left .sb-mark { margin-top: 1.6mm; }
.head-right { justify-self: end; display: flex; align-items: flex-start; gap: 2.5mm; }
.title-box { border: 2px solid #000; padding: 1mm 4mm; min-width: 56mm; box-sizing: border-box; font-size: 17px; font-weight: 700; letter-spacing: 2px; text-align: center; white-space: nowrap; }
.title-box-sm { font-size: 15px; padding: 0.8mm 3mm; min-width: 42mm; }

/* meta label/value boxes (発注日, 注文No., 納期, 取引先コード) */
.meta-box { border-collapse: collapse; }
.meta-box th, .meta-box td { border: 1px solid #000; text-align: center; padding: 0.3mm 2.5mm; font-weight: 400; white-space: nowrap; }
.meta-box th { font-size: 9px; }
.meta-box td { font-size: 11.5px; min-width: 21mm; }
.meta-box th.thick { border: 2px solid #000; border-bottom-width: 1px; }
.meta-box td.thick { border: 2px solid #000; border-top-width: 1px; }

/* party blocks */
.parties { display: flex; justify-content: space-between; align-items: flex-start; gap: 6mm; font-size: 10px; line-height: 1.3; margin-bottom: 4.5mm; }
.parties-nouhin { padding-right: 46mm; margin-bottom: 1.5mm; }
.party { min-width: 0; }
.party-mid { flex: 1; }
.party-name { font-size: 12px; font-weight: 600; padding: 0.8mm 0 0.4mm; }
.party-contact { padding-top: 1mm; }
.party-pad { padding-top: 1mm; }
.party-tel { padding-left: 2mm; }
.telgap { display: inline-block; width: 12mm; }
.party-two { display: grid; grid-template-columns: auto auto; justify-content: start; column-gap: 16mm; }

/* stamp boxes */
.table-wrap { position: relative; }
.stamp-abs { position: absolute; right: 0; top: -4.5mm; z-index: 1; background: #fff; }
.stamp-grid { border-collapse: collapse; }
.stamp-grid td { border: 1px solid #000; width: 16mm; height: 16mm; background: #fff; }
.band-a-wrap { width: 72.7%; }
.stamp-square { width: 14mm; height: 14mm; border: 1px solid #000; margin-top: 1mm; flex: none; align-self: flex-start; }

/* item tables */
.doc-t { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: -1px; }
.doc-t th, .doc-t td { border: 1px solid #000; padding: 0.3mm 1.5mm; font-size: 10px; font-weight: 400; text-align: left; vertical-align: top; overflow: hidden; }
.doc-t th { text-align: center; font-size: 9px; padding: 0.3mm 1mm; white-space: nowrap; }
.doc-t th.th-sm { font-size: 8.5px; }
.doc-t .tall2 td { height: 7.5mm; }
.doc-t .tallA td { height: 9mm; }
.doc-t .tall1 { height: 4mm; }
.doc-t .vbottom { vertical-align: bottom; }
.doc-t .vtop { vertical-align: top; }
.doc-t .vmiddle { vertical-align: middle; }
.doc-t .center { text-align: center; }
.doc-t .num { text-align: right; }
.doc-t .left { text-align: left; }
.doc-t .sm { font-size: 9px; padding: 0.2mm 0.5mm; }
.doc-t .kanno { font-size: 9px; white-space: nowrap; padding: 0.2mm 0.3mm; letter-spacing: -0.5px; }
.doc-t .cell-lbl { font-size: 9.5px; }
.doc-t .big-num td { font-size: 13px; vertical-align: middle; }
.pk-sub { font-size: 7.5px; font-weight: 400; margin-left: 0.8mm; color: #27272a; }
.pk-sub-block { display: block; margin-left: 0; white-space: nowrap; }
.band-a { margin-top: 0; }

/* 発注数 band: single table — open 備考 area cells are borderless */
.doc-t td.open-mid { border: none; }
.doc-t td.open-bot { border: none; border-bottom: 1px solid #000; }

/* 入荷 block (thick border) + 消費税 column */
.inbound-row { display: flex; align-items: stretch; margin-top: -1px; }
.inbound-row .stamp-square + .thick-block { margin-left: 4mm; }
.thick-block { border: 2px solid #000; flex: 1; }
.thick-block th, .thick-block td { border: 1px solid #000; }
.tax-col { width: 16.2%; flex: none; margin-left: -2px; }
.inquiry { font-size: 10px; margin-top: 0.5mm; }

/* barcodes */
.barcode { height: 7mm; }
.barcode-nouhin { margin: 0.5mm 0 1mm 12mm; }
.barcode-genpin { display: block; margin: 0 2mm 0.5mm auto; height: 6.5mm; }

/* 受領書 / 現品票 pair */
.doc-pair { display: flex; gap: 8mm; align-items: flex-start; }
.doc-half { flex: 1; min-width: 0; }
.half-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1mm; }
.half-head-right { display: flex; align-items: flex-start; gap: 2mm; }
.half-head-right .sb-mark { margin-top: 1.2mm; }
.half-head-right .meta-box td { min-width: 22mm; font-size: 11.5px; }
.half-vendor { font-size: 11.5px; margin: 0.5mm 0 0.8mm; }
.half-code { font-size: 11px; }
.biko-row { border: 1px solid #000; border-bottom: none; font-size: 9.5px; padding: 0.4mm 1.5mm; margin-top: -1px; }
.half-footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 1mm; font-size: 10.5px; line-height: 1.5; }
.half-footer .stamp-grid td { width: 14mm; }
.genpin-footer { justify-content: flex-end; }
.stamp-square-sm { width: 14mm; height: 14mm; margin-top: 0.5mm; margin-left: 2mm; }

/* cut line */
.cut-line { border-top: 1px dotted #999; margin: 1.6mm 0; }

/* Sensbey branding (text only) */
.sb-mark { display: inline-block; font-size: 8px; letter-spacing: 3px; color: #52525b; font-weight: 600; font-family: Verdana, "Yu Gothic", sans-serif; }
.sb-footer { margin-top: auto; padding-top: 0.5mm; border-top: 1px solid #d4d4d8; display: flex; justify-content: space-between; align-items: flex-end; }
.sb-footer-text { display: flex; flex-direction: column; gap: 0.8mm; font-size: 7px; color: #71717a; letter-spacing: 0.03em; }
.sb-qr { width: 7mm; height: 7mm; }

@media print {
  @page { size: A4 portrait; margin: 0; }
  .doc-page { box-shadow: none; margin: 0; width: 210mm; height: 287mm; min-height: 0; overflow: hidden; page-break-after: always; break-after: page; }
  .doc-page:last-child { page-break-after: auto; break-after: auto; }
  .no-print { display: none !important; }
}
`;
